import { useCallback, useEffect, useRef } from 'react';
import { ErrorPanel, LoadingIndicator } from '@nbirl/nbi-common-ui-libs';

import { ApiError } from '../../../../shared/models';
import { getErrorCodeMessage } from '../../../../shared/utils';
import { TABLEAU_ATTRIBUTES } from '../../constants';
import { useDashboards } from '../../context';
import { DashboardWorkbook } from '../../models';
import { useDashboardTicketQuery } from '../../queries';
import { buildTableauUrl } from '../../utils';

export const DashboardsTableau = () => {
  const tableauVizNodeRef = useRef<HTMLDivElement>(null);
  const tableauVizRef = useRef<tableau.Viz>();

  const {
    workbookUrl,
    selectedWorkbook,
    selectWorkbooksViewByContentUrl,
    tableauBaseUrlProps,
  } = useDashboards();

  const {
    isError,
    isFetching,
    error,
    refetch: refetchTicket,
  } = useDashboardTicketQuery();

  const handleFirstVizSizeKnown = useCallback(
    (event: tableau.VizResizeEvent) => {
      if (tableauVizRef.current) {
        const { sheetSize, chromeHeight = 0 } = event.getVizSize();
        const width = sheetSize.minSize.width + chromeHeight;
        const height = sheetSize.minSize.height + chromeHeight;
        tableauVizRef.current.setFrameSize(width, height);
      }
    },
    []
  );

  const onMarksSelection = useCallback(
    async (e: tableau.MarksEvent) => {
      const marks: tableau.Mark[] = await e.getMarksAsync();
      // Marks[0] does not exist when the mark is being deselected
      const attrPairs: tableau.Pair[] = marks[0]?.getPairs() || [];
      const contentUrlAttr = attrPairs.find(
        (attr) => attr.fieldName === TABLEAU_ATTRIBUTES.contentUrl
      );
      if (contentUrlAttr) {
        selectWorkbooksViewByContentUrl(contentUrlAttr.value.toString());
      }
    },
    [selectWorkbooksViewByContentUrl]
  );

  const createTableauViz = useCallback(
    async (wb: DashboardWorkbook, filters?: URLSearchParams) => {
      const ticketResponse = await refetchTicket();

      if (ticketResponse.data?.ticket && tableauVizNodeRef.current) {
        tableauVizRef.current = new window.tableau.Viz(
          tableauVizNodeRef.current,
          buildTableauUrl(
            tableauBaseUrlProps,
            ticketResponse.data.ticket,
            wb,
            filters
          ),
          {
            width: '1200px',
            height: '900px',
            hideToolbar: true,
            onFirstVizSizeKnown: handleFirstVizSizeKnown,
          }
        );
        tableauVizRef.current.addEventListener(
          tableau.TableauEventName.MARKS_SELECTION,
          onMarksSelection
        );
      }
    },
    [
      handleFirstVizSizeKnown,
      onMarksSelection,
      refetchTicket,
      tableauBaseUrlProps,
    ]
  );

  useEffect(() => {
    if (selectedWorkbook) {
      createTableauViz(selectedWorkbook, workbookUrl.filters);
    }

    return () => {
      tableauVizRef.current?.dispose();
    };
  }, [createTableauViz, selectedWorkbook, workbookUrl.filters]);

  if (isFetching || isError) {
    return (
      <div className="h-full flex justify-center items-center">
        {isFetching && <LoadingIndicator />}
        {isError && (
          <ErrorPanel error={getErrorCodeMessage(error as ApiError)} />
        )}
      </div>
    );
  }

  return (
    <div className="pt-6">
      <div ref={tableauVizNodeRef} />
    </div>
  );
};
